import { Close, Delete } from '@mui/icons-material';
import { Box, Button, Divider, IconButton, Modal, Stack } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { ReactComponent as FileIcon } from '../../../../assets/icons/Files.svg';
import { useDispatch, useSelector } from 'react-redux';
import { useMessage } from '../../../../base/MessageProvider';
import ReactTextInput from '../../../../components/ControllerComponent/ReactTextInput';
import ReactiveDatePickerField from '../../../../components/ControllerComponent/ReactDatePicker';
import clsx from 'clsx';
import { addFameData, deleteFameData, getProfileData, updateFameData } from '../../../actions/profile';
import { Bars } from 'react-loader-spinner';
import { getOAuthData } from '../../../network/getOAuthData';

const boxStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: 400,
    width: '90%',
    bgcolor: 'white',
    paddingX: 5,
    paddingY: 3,
    borderRadius: '18px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center' 
}

const validateSchema = Yup.object().shape({
    title: Yup.string().required("Title is required"),
    launchDate: Yup.string().required("Date is required"),
});

const FameForm = ({ open, onClose, onSuccess, metaData }) => {
    const [image, setImage] = useState(null);
    const [url, setUrl] = useState(null)
    const [dragActive, setDragActive] = useState(false);

    const dispatch = useDispatch();
    const inputRef = useRef(null); 
    const alert = useMessage();
    const loading = useSelector((state) => state.recruiterProfileReducer?.loading);

    const {
        control,
        handleSubmit,
        setValue,
        clearErrors,
        formState: { errors }
    } = useForm({
        defaultValues: {
            title: '',
            launchDate: new Date(),
        },
        resolver: yupResolver(validateSchema)
    });

    useEffect(() => {
        const init = async () => {
            setUrl(null);
            if (metaData?.media?.url)
                setUrl(await getOAuthData(metaData?.media?.url));
            setValue('title', metaData?.title ?? '');
            setValue('launchDate', metaData?.launchDate ? new Date(metaData?.launchDate) : null);
        }
        init();
    }, [metaData]);

    const handleDrag = function(e) {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };
      
    const handleDrop = function(e) {
        setImage(null)
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            if (e.dataTransfer.files[0].type !== 'image/jpg' && e.dataTransfer.files[0].type !== 'image/jpeg' && e.dataTransfer.files[0].type !== 'image/png') {
                alert.showInfo('Accepts only JPG/PNG file!!');
                return;
            }
            setImage(e.dataTransfer.files[0]);
            setUrl(URL.createObjectURL(e.dataTransfer.files[0]))
        }
    };
    
    const handleChange = function(e) {
        e.preventDefault();
        if (e.target.files && e.target.files[0]) {
            setImage(e.target.files[0]);
            setUrl(URL.createObjectURL(e.target.files[0]))
        }
    };
    
    const onChooseButtonClick = () => {
        inputRef.current.click();
    };

    const successCallback = async (res) => {
        alert.showSuccess(res.message);
        dispatch(getProfileData());
        onClose();
        onSuccess();
    }

    const errorCallback = async (error) => {
        alert.showError(error?.message ?? "Something went wrong!!");
    }

    const addHandler = async (data) => {
        dispatch(addFameData({ ...data, image }, successCallback, errorCallback));
    }

    const updateHandler = async (data) => {
        dispatch(updateFameData({ ...data, image, fameId: metaData?.fameId }, successCallback, errorCallback));
    }

    const deleteHandler = async () => {
        dispatch(deleteFameData({ fameId: metaData?.fameId }, successCallback, errorCallback));
    }

    const onClickDeleteButton = () => {
        alert.showConfirmation2({
            message: 'Are you sure you want to delete?',
            button1Title: 'Cancel',
            button2Title: 'Delete',
            onPressBtn1: () => {},
            onPressBtn2: deleteHandler,
        })
    }

    const onSubmit = async (data) => {
        if (!url) {
            alert.showInfo("Please select image!!");
            return;
        }
        if (metaData) updateHandler(data);
        else addHandler(data);
    }

    return (
        <Modal disableAutoFocus sx={{ bgcolor: '#F4F4F499'}} hideBackdrop open={open}>
            <Box className="shadow-[0_0_14px_grey]" sx={boxStyle}>
                <IconButton onClick={onClose} className='absolute top-3 right-3' size='small'> 
                    <Close className='text-xl' />
                </IconButton>
                <Box className="mb-6">
                    <h2 className="font-['Poppins-Medium'] font-[500] text-center text-[16px]">{!metaData ? 'Add' : 'Edit'} Known For</h2>
                </Box>
                <div className="relative w-[200px] h-[200px] flex flex-col justify-center items-center my-3 border-[1px] border-[#EDECEC]">
                    {!url ? (
                        <label htmlFor="excel" className={clsx("w-full h-full rounded-[8px] py-[35px] px-2 flex flex-col justify-center items-center gap-1", {
                            "bg-[#F6F6F6]": !dragActive,
                            "bg-[#e7e7e7]": dragActive,
                        })} 
                            onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}>
                            <input ref={inputRef} className="hidden" type="file" id="image-file" onChange={handleChange} accept="image/png, image/jpeg" required/>
                            <FileIcon />
                            <p className="max-w-[140px] mt-7 font-['Poppins-Regular'] font-[400] text-[12px] text-center text-[#1F1E1E]">
                                Drag Your Photo Here Or <a onClick={onChooseButtonClick} className="text-[#FF4B5A] hover:underline cursor-pointer">Click For Browse</a>
                            </p>
                            <p className="font-['Poppins-Regular'] font-[400] text-[10px] text-center text-[#7E7878]">Jpg, Png</p>
                        </label>
                    ) : (
                        <Button onClick={onChooseButtonClick} className="p-0">
                            <input ref={inputRef} className="hidden" type="file" id="image-file" onChange={handleChange} accept="image/png, image/jpeg" required/>
                            <img className="h-full object-cover aspect-square" src={url} />
                        </Button>
                    )}
                </div>
                <Stack className="w-full" my={1} spacing={2}>
                    <ReactTextInput
                        hookFormControl={control}
                        formLabel="Title*"
                        varName="title"
                        key={'name'}
                        placeholder="Eg. Tumbadd, Beyond The Clouds"
                        errorMessage={errors?.title?.message}
                    />
                    <ReactiveDatePickerField
                        hookFormControl={control}
                        formLabel="Launch Date*"
                        varName={'launchDate'}
                        errorMessage={errors?.launchDate?.message}
                        maxDate={new Date()}
                    />
                </Stack>
                <Stack className="w-full" alignItems={'center'} direction={'row'} spacing={1}>
                    <Button onClick={handleSubmit(onSubmit)} disabled={loading} className={clsx("bg-[#2F80ED] flex gap-3 justify-center text-[#FFF] w-full rounded-full my-2 font-['Poppins-Medium'] font-[500]", { "bg-opacity-70": loading })}>
                        Submit
                        <Bars
                            height="20"
                            width={'20'}
                            color="#FFFFFF"
                            ariaLabel="bars-loading"
                            visible={loading}
                        />
                    </Button>
                    {metaData && (
                        <IconButton onClick={onClickDeleteButton} size='small' className="bg-[red] w-[39px] h-[39px]">
                            <Delete className='text-white text-xl' />
                        </IconButton>
                    )}
                </Stack>
            </Box>
        </Modal>
    );
}

export default React.memo(FameForm, (prevProps, nextProps) => {
    return prevProps.open === nextProps.open
});